// src/components/enterprise/EarlyWarningPanel.jsx
//
// "Where it is heading": the so-what and the now-what. One row per visible
// group, the fastest-rising first: the share screening positive today with its
// four-week change in points, the share newly positive since their previous
// check-in, the share whose wellbeing fell ten points or more, what leads (the
// screener with the largest share at moderate or worse), a pace reading
// against the population line (modeled: a straight line through two points)
// and the step the driver calls for. Everything but the pace is observed,
// aggregate and floored server-side; a dash is a denominator under the floor.
// The panel renders nothing until the snapshot carries direction (the
// function deployed 2026-11-03), so the modeled example stays as it was.

import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus, Lock, ArrowRight, Compass } from 'lucide-react';
import { ConfidenceTag, SourceTag, MIN_GROUP_SIZE } from './shared/measurement';
import { useEdition } from '../../data/editions/EditionContext';
import { earlyWarningRows, hasDirection, pts, POPULATION_LINE } from '../../utils/earlyWarning';

const Delta = ({ value }) => {
    if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
    if (value === 0) return <span className="inline-flex items-center gap-0.5 text-slate-500"><Minus size={11} /> 0 pts</span>;
    const up = value > 0;
    return (
        <span className={`inline-flex items-center gap-0.5 font-semibold ${up ? 'text-rose-600' : 'text-emerald-700'}`}>
            {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{up ? '+' : '−'}{Math.abs(value)} pts
        </span>
    );
};

const Share = ({ value }) => (value === null || value === undefined ? <span className="text-slate-400">—</span> : <span className="font-semibold text-slate-800">{pts(value)}%</span>);

const PACE_TONE = {
    above: 'text-rose-700 bg-rose-50 border-rose-100',
    rising: 'text-amber-700 bg-amber-50 border-amber-100',
    steady: 'text-slate-600 bg-slate-50 border-slate-200',
    easing: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    unknown: 'text-slate-500 bg-slate-50 border-slate-200'
};

const EarlyWarningPanel = ({ live, onNavigate }) => {
    const edition = useEdition();
    if (!hasDirection(live)) return null;
    const rows = earlyWarningRows(live, edition);
    if (rows.length === 0) return null;
    const weeks = rows[0].momentum.weeks;

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 md:px-8 pt-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Compass size={16} className="text-[var(--brand-blue)]" />
                        <h3 className="text-base font-semibold text-[var(--brand-deep)]">Where it is heading</h3>
                        <SourceTag modeled={false} />
                        <ConfidenceTag level="Leading" />
                    </div>
                    <p className="text-xs text-slate-500 font-light mt-1 max-w-3xl">
                        Each group&rsquo;s share screening positive against four weeks ago, who moved since their previous check-in, what leads, and the step it calls for.
                        Direction reads each group against the {pts(POPULATION_LINE)}% of Canadian adults who screen positive (Statistics Canada) and runs its four-week trend straight ahead: modeled, not a forecast.
                    </p>
                </div>
                {onNavigate && (
                    <button onClick={() => onNavigate('cc-readiness')} className="flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-blue)] hover:gap-2 transition-all shrink-0">
                        See the strain detail <ArrowRight size={13} />
                    </button>
                )}
            </div>

            <div className="p-6 md:p-8 overflow-x-auto">
                <table className="w-full text-xs min-w-[760px]">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                            <th className="text-left font-semibold pb-2">Group</th>
                            <th className="text-right font-semibold pb-2">Screening positive</th>
                            <th className="text-right font-semibold pb-2">{weeks}-week change</th>
                            <th className="text-right font-semibold pb-2">Newly positive</th>
                            <th className="text-right font-semibold pb-2">Worsening 10+ pts</th>
                            <th className="text-left font-semibold pb-2 pl-4">What leads</th>
                            <th className="text-left font-semibold pb-2">Direction</th>
                            <th className="text-left font-semibold pb-2 pl-3">Next step</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.name} className="border-t border-slate-100 align-top">
                                <td className="py-3 pr-3 font-semibold text-slate-700">
                                    {r.name}
                                    <span className="block text-[10px] text-slate-400 font-normal">{r.responses} respondents</span>
                                </td>
                                <td className="py-3 text-right tabular-nums font-bold text-slate-800">{pts(r.momentum.now)}%</td>
                                <td className="py-3 text-right tabular-nums"><Delta value={r.momentum.deltaPts} /></td>
                                <td className="py-3 text-right tabular-nums"><Share value={r.movement ? r.movement.newlyPositive : null} /></td>
                                <td className="py-3 text-right tabular-nums"><Share value={r.movement ? r.movement.worsening : null} /></td>
                                <td className="py-3 pl-4">
                                    {r.driver ? (
                                        <>
                                            <span className="font-semibold text-slate-700">{r.driver.label}</span>
                                            <span className="block text-[10px] text-slate-400">{pts(r.driver.share)}% · {r.driver.note}</span>
                                        </>
                                    ) : <span className="text-slate-400">Nothing leads</span>}
                                </td>
                                <td className="py-3 pr-3 max-w-[14rem]">
                                    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded border ${PACE_TONE[r.pace.kind] || PACE_TONE.unknown}`}>{r.pace.label}</span>
                                    <span className="block text-[10px] text-slate-400 mt-1 leading-relaxed">{r.pace.detail}</span>
                                </td>
                                <td className="py-3 pl-3 text-slate-600 leading-relaxed max-w-[20rem]">{r.nextStep}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="text-[10px] text-slate-400 mt-3 leading-relaxed flex items-start gap-1">
                    <Lock size={10} className="mt-0.5 shrink-0" />
                    Groups of {MIN_GROUP_SIZE} or more; a dash is a denominator under the floor. Newly positive: respondents clear at their previous check-in who screen positive now.
                    Worsening: wellbeing down ten points or more since their previous check-in, at least two weeks apart. Nothing here describes a person.
                </p>
            </div>
        </div>
    );
};

export default EarlyWarningPanel;
